import { PAGE_LIMIT } from '../../../constants';
import { API_CALL } from '../../../middleware/api';
import { ORGANIZATION_LIST } from '../../ShipperCaps/redux/constants';
import {
  ASSIGN_CAPS_TO_THE_ORGANIZATION,
  CHANGE_ORG_ADMINISTRATIVE_STATE,
  CLEAR_DATA,
  CLEAR_ERRORS,
  CLEAR_SUGGESTED_DATA,
  CREATE_ORAGANIZATION_GROUP,
  DELETE_HUB_DEVICE,
  DELETE_ORGANIZATION,
  EDIT_ORAGANIZATION_GROUP,
  GET_ALL_PERMISSION,
  GET_ORG_GROUP_USERS,
  GET_ORAGANIZATION,
  GET_ORAGANIZATION_QUERY,
  GET_ORAGANIZATION_GROUPS,
  GET_ORAGANIZATION_REPORT,
  GET_ORG_CONFIG,
  GET_ORG_TEMPLATES,
  GET_ORG_ROLES_DETAIL,
  GET_ORG_STATE_HISTORY,
  GET_ROLES_DETAIL,
  HUB_DEVICES,
  HUB_DEVICES_TYPES,
  LAUNCH_ORG,
  SAVE_ORG_TEMPLATE,
  LINK_ORGANIZATION,
  ORGANIZATION_DEVICES,
  UNLINK_ORGANIZATION,
  UNSUSPEND_ORG,
  UPDATE_ORG,
  UPDATE_ORG_ROLES_DETAIL,
  ADD_USER_TO_ORG_GROUP,
  REMOVE_USER_FROM_ORG_GROUP,
  DELETE_ORG_GROUP,
  DELETE_ORG_TEMPLATE,
  GET_REMINDER_CONFIGURATION,
  GET_EFFECTIVE_REMINDER_CONFIGURATION,
  PATCH_REMINDER_CONFIGURATION,
  PREVIEW_REMINDER_CONFIGURATION,
  GET_CARE_PROVIDERS,
  GET_MANDATORY_TASKS,
  GET_ORG_DELETION_READINESS,
  GET_ORG_TEMPLATE_CONFIG,
  GET_ORG_TEMPLATE_ROLES,
  EDIT_MANDATORY_TASKS,
  UPDATE_ORG_TEMPLATE_ROLES,
  UPDATE_ORG_TEMPLATE,
  GET_BLOCKING_ENROLLMENTS,
} from './constants';

const normalizeGetOrgsParams = requestOrType => {
  if (!requestOrType) {
    return undefined;
  }

  if (typeof requestOrType === 'string') {
    return { filterBy: [`org_type:${requestOrType}`] };
  }

  if (!requestOrType.type) {
    return requestOrType;
  }

  const { type, filterBy, ...rest } = requestOrType;
  const existingFilterBy = Array.isArray(filterBy) ? filterBy : filterBy ? [filterBy] : [];
  return {
    ...rest,
    filterBy: [...existingFilterBy, `org_type:${type}`],
  };
};

export const actions = {
  downloadReport: () => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: `portal/v1/organization/caps/report`,
      },
      type: GET_ORAGANIZATION_REPORT,
    };
  },
  // The organizations table's list (initial load, refetch after a save or delete): only its
  // newest request may fill the table state.
  getOrganizations: pageRequest => ({
    [API_CALL]: {
      method: 'GET',
      endpoint: `portal/v1/organizations/`,
      params: { ...pageRequest, limit: pageRequest?.limit ?? PAGE_LIMIT },
      latest: true,
    },
    type: GET_ORAGANIZATION,
  }),
  // The same endpoint for callers that only read the response (dropdowns, details). Its own
  // action type keeps their results out of the organizations table state.
  queryOrganizations: pageRequest => ({
    [API_CALL]: {
      method: 'GET',
      endpoint: `portal/v1/organizations/`,
      params: { ...pageRequest, limit: pageRequest?.limit ?? PAGE_LIMIT },
    },
    type: GET_ORAGANIZATION_QUERY,
  }),
  deleteOrg: (id, forceClean = false) => ({
    [API_CALL]: {
      method: 'POST',
      endpoint: `portal/v1/organizations/${id}/state/administrative`,
      data: { state: 'Deleted', force_clean: forceClean },
    },
    type: DELETE_ORGANIZATION,
  }),
  getOrgDeletionReadiness: id => ({
    [API_CALL]: {
      method: 'GET',
      endpoint: `portal/v1/organizations/${id}/state/deletion-readiness`,
    },
    type: GET_ORG_DELETION_READINESS,
  }),

  deleteHubDevice: (hubId, deviceId, callAfterSuccess = []) => ({
    [API_CALL]: {
      endpoint: `portal/v1/hubs/${hubId}/devices/${deviceId}`,
      method: 'DELETE',
      callAfterSuccess,
    },
    type: DELETE_HUB_DEVICE,
  }),

  getDevices: pageRequest => ({
    [API_CALL]: {
      endpoint: `portal/v1/devices/unassigned/`,
      method: 'get',
      params: { limit: PAGE_LIMIT, ...pageRequest },
    },
    type: ORGANIZATION_DEVICES,
  }),

  getHubDevices: (hubId, pageRequest) => ({
    [API_CALL]: {
      endpoint: `portal/v1/hubs/${hubId}/devices/`,
      method: 'get',
      params: { limit: PAGE_LIMIT, ...pageRequest },
    },
    type: HUB_DEVICES,
  }),

  getDeviceTypes: pageRequest => ({
    [API_CALL]: {
      endpoint: `portal/devices/event-types`,
      method: 'get',
      params: { limit: PAGE_LIMIT, ...pageRequest },
    },
    type: HUB_DEVICES_TYPES,
  }),

  getOrgs: requestOrType => ({
    [API_CALL]: {
      method: 'GET',
      endpoint: 'portal/v1/orgs/',
      params: normalizeGetOrgsParams(requestOrType),
    },
    type: ORGANIZATION_LIST,
  }),

  assignCaps: data => ({
    [API_CALL]: {
      endpoint: `portal/v1/organizations/${data.organization_id}/devices/`,
      method: 'PUT',
      data: data,
    },
    type: ASSIGN_CAPS_TO_THE_ORGANIZATION,
  }),

  getOrgConfig: pageRequest => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: `portal/v1/organizations/${pageRequest.organizationId}/configs`,
      },
      type: GET_ORG_CONFIG,
    };
  },

  getOrgTemplateConfig: pageRequest => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: `portal/v1/organizations/templates/${pageRequest.organizationId}`,
      },
      type: GET_ORG_TEMPLATE_CONFIG,
    };
  },

  getOrgTemplates: (pageRequest = {}) => ({
    [API_CALL]: {
      method: 'GET',
      endpoint: 'portal/v1/organizations/templates/',
      params: pageRequest,
    },
    type: GET_ORG_TEMPLATES,
  }),

  saveOrgTemplate: (organizationId, name, ownerUuid) => ({
    [API_CALL]: {
      method: 'POST',
      endpoint: `portal/v1/organizations/templates/${organizationId}`,
      data: {
        name,
        ...(ownerUuid !== undefined ? { owner_uuid: ownerUuid } : {}),
      },
    },
    type: SAVE_ORG_TEMPLATE,
  }),
  createOrgTemplate: data => ({
    [API_CALL]: {
      method: 'POST',
      endpoint: 'portal/v1/organizations/templates/',
      data,
    },
    type: SAVE_ORG_TEMPLATE,
  }),

  deleteOrgTemplate: templateUuid => ({
    [API_CALL]: {
      method: 'DELETE',
      endpoint: `portal/v1/organizations/templates/${templateUuid}`,
    },
    type: DELETE_ORG_TEMPLATE,
  }),

  getOrgRole: pageRequest => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: `portal/v1/organizations/${pageRequest.organizationId}/roles`,
      },
      type: GET_ROLES_DETAIL,
    };
  },

  getOrgTemplateRoles: pageRequest => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: `portal/v1/organizations/templates/${pageRequest.organizationId}/roles`,
      },
      type: GET_ORG_TEMPLATE_ROLES,
    };
  },

  updateOrgTemplateRoles: (data, pageRequest) => ({
    [API_CALL]: {
      endpoint: `portal/v1/organizations/templates/${pageRequest.organizationId}/roles`,
      method: 'PUT',
      data: data,
    },
    type: UPDATE_ORG_TEMPLATE_ROLES,
  }),

  getTemplateOrgConfig: pageRequest => {
    const endpoint = pageRequest?.isTemplate
      ? 'portal/v1/organizations/schema'
      : `portal/v1/organizations/${pageRequest.organizationId}/schema`;
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint,
      },
      type: GET_ALL_PERMISSION,
    };
  },

  getOrgRoleConfig: pageRequest => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: `portal/v1/organizations/${pageRequest.organizationId}/permissions/${pageRequest.roleUuid}`,
      },
      type: GET_ORG_ROLES_DETAIL,
    };
  },

  updateOrgRoleConfig: (data, pageRequest) => ({
    [API_CALL]: {
      endpoint: `portal/v1/organizations/${pageRequest.organizationId}/permissions/${pageRequest.roleUuid}`,
      method: 'PUT',
      data: data,
    },
    type: UPDATE_ORG_ROLES_DETAIL,
  }),

  updateOrg: (data, pageRequest) => ({
    [API_CALL]: {
      endpoint: `portal/v1/organizations/${pageRequest.organizationId}`,
      method: 'PUT',
      data: data,
    },
    type: UPDATE_ORG,
  }),

  updateOrgTemplate: (data, pageRequest) => ({
    [API_CALL]: {
      endpoint: `portal/v1/organizations/templates/${pageRequest.organizationId}`,
      method: 'PUT',
      data: data,
    },
    type: UPDATE_ORG_TEMPLATE,
  }),
  updateOrgTemplateEdit: (data, pageRequest) => ({
    [API_CALL]: {
      endpoint: `portal/v1/organizations/templates/${pageRequest.organizationId}/edit`,
      method: 'PUT',
      data: data,
    },
    type: UPDATE_ORG_TEMPLATE,
  }),

  getOrganizationStateHistory: id => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: `portal/v1/organizations/${id}/state/history`,
      },
      type: GET_ORG_STATE_HISTORY,
    };
  },

  changeOrganizationAdministrativeState: (id, data) => {
    return {
      [API_CALL]: {
        method: 'POST',
        endpoint: `portal/v1/organizations/${id}/state/administrative`,
        data,
      },
      type: CHANGE_ORG_ADMINISTRATIVE_STATE,
    };
  },

  launchOrganization: id => {
    return {
      [API_CALL]: {
        method: 'POST',
        endpoint: `portal/v1/organizations/${id}/state/business/launch`,
      },
      type: LAUNCH_ORG,
    };
  },

  unsuspendOrganization: (id, data) => {
    return {
      [API_CALL]: {
        method: 'POST',
        endpoint: `portal/v1/organizations/${id}/state/administrative/unsuspend`,
        data,
      },
      type: UNSUSPEND_ORG,
    };
  },

  linkOrganization: (orgId, data) => {
    return {
      [API_CALL]: {
        method: 'PUT',
        endpoint: `portal/v1/organizations/${orgId}/providers`,
        data,
      },
      type: LINK_ORGANIZATION,
    };
  },

  unlinkOrganization: (orgId, linkedOrgId, params = null) => {
    return {
      [API_CALL]: {
        method: 'DELETE',
        endpoint: `portal/v1/organizations/${orgId}/providers/${linkedOrgId}`,
        params,
      },
      type: UNLINK_ORGANIZATION,
    };
  },

  
  getBlockingEnrollments: (orgId, provider_org_uuid) => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: `portal/v1/organizations/${orgId}/providers/${provider_org_uuid}/blocking-enrollments`,
               // /portal/v1/organizations/{org_uuid}/providers/{provider_org_uuid}/blocking-enrollments
      },
      type: GET_BLOCKING_ENROLLMENTS,
    };
  },

  getOrganizationGroups: pageRequest => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: 'portal/v1/organizations/groups',
        params: pageRequest?.limit ? { ...pageRequest } : { ...pageRequest, limit: 1000 },
      },
      type: GET_ORAGANIZATION_GROUPS,
    };
  },

  createOrganizationGroup: data => {
    return {
      [API_CALL]: {
        method: 'POST',
        endpoint: 'portal/v1/organizations/groups',
        data: data,
      },
      type: CREATE_ORAGANIZATION_GROUP,
    };
  },

  deleteOrganizationGroup: id => {
    return {
      [API_CALL]: {
        method: 'DELETE',
        endpoint: `portal/v1/organizations/groups/${id}`,
      },
      type: DELETE_ORG_GROUP,
    };
  },

  editOrganizationGroup: (id, data) => {
    return {
      [API_CALL]: {
        method: 'PUT',
        endpoint: `portal/v1/organizations/groups/${id}`,
        data: data,
      },
      type: EDIT_ORAGANIZATION_GROUP,
    };
  },

  getGroupUsers: group_id => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: `portal/v1/organizations/groups/${group_id}/users`,
      },
      type: GET_ORG_GROUP_USERS,
    };
  },
  getCareProviders: params => {
    return {
      [API_CALL]: {
        method: 'GET',
        endpoint: 'portal/v1/organizations/providers',
        params,
      },
      type: GET_CARE_PROVIDERS,
    };
  },

  addUserToOrgGroup: (group_id, user_id) => {
    return {
      [API_CALL]: {
        method: 'POST',
        endpoint: `portal/v1/organizations/groups/${group_id}/users/${user_id}`,
      },
      type: ADD_USER_TO_ORG_GROUP,
    };
  },

  removeUserFromOrgGroup: (group_id, user_id) => {
    return {
      [API_CALL]: {
        method: 'DELETE',
        endpoint: `portal/v1/organizations/groups/${group_id}/users/${user_id}`,
      },
      type: REMOVE_USER_FROM_ORG_GROUP,
    };
  },

  getEffectiveReminderConfiguration: (orgId, language) => ({
    [API_CALL]: {
      endpoint: 'portal/reminders/default_messages/configuration/effective',
      method: 'GET',
      params: { orgId, language },
    },
    type: GET_EFFECTIVE_REMINDER_CONFIGURATION,
  }),
  getReminderConfiguration: (orgId, language, storageType) => ({
    [API_CALL]: {
      endpoint: `portal/reminders/default_messages/configuration/${orgId ? `organizations/${orgId}` : 'global'}`,
      method: 'GET',
      params: { language, ...(storageType ? { storage_type: storageType } : {}) },
    },
    type: GET_REMINDER_CONFIGURATION,
  }),
  patchReminderConfiguration: (orgId, data) => ({
    [API_CALL]: {
      endpoint: `portal/reminders/default_messages/configuration/${orgId ? `organizations/${orgId}` : 'global'}`,
      method: 'PATCH',
      data,
    },
    type: PATCH_REMINDER_CONFIGURATION,
  }),
  previewReminderConfiguration: (orgId, data) => ({
    [API_CALL]: {
      endpoint: `portal/reminders/default_messages/configuration/${orgId ? `organizations/${orgId}` : 'global'}/preview`,
      method: 'POST',
      data,
    },
    type: PREVIEW_REMINDER_CONFIGURATION,
  }),
  
  getMandatoryTasks: params => ({
    [API_CALL]: {
      method: 'GET',
      endpoint: `portal/billing/work_log/mandatory`,
      params,
    },
    type: GET_MANDATORY_TASKS,
  }),

  editMandatoryTasks: data => ({
    [API_CALL]: {
      endpoint: `portal/billing/work_log/mandatory`,
      method: 'PUT',
      data,
    },
    type: EDIT_MANDATORY_TASKS,
  }),

  clearData: () => ({
    type: CLEAR_DATA,
  }),

  clearErrors: () => ({
    type: CLEAR_ERRORS,
  }),

  clearAutoSuggestedDevice: () => ({
    type: CLEAR_SUGGESTED_DATA,
  }),
};
